import ContentLoader from 'react-content-loader'
import { Link } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { IconButtonNext } from 'components/Icons/IconButtonNext'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { ROUTES } from 'utils/route-constants'
import { LpVaultListRow } from '../types'
import { getFormattedUsdValue } from 'utils/usdFormatters'

interface LpVaultRowProps {
  displayInfo: LpVaultListRow
  isLast: boolean
  isLoading: boolean
}

const IconNextContainer = styled.div`
  display: flex;
  position: absolute;
  right: 40px;
  cursor: pointer;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const VaultRowWrapper = styled.div<{ $isLast?: boolean }>`
  position: relative;
  padding: 24px 32px;
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  &:hover {
    background-color: ${p => p.theme.colors.background.active};
    ${p => p.$isLast && `border-radius: 0 0 32px 32px`};
    & > div > svg > rect {
      fill: ${p => p.theme.colors.button.background};
    }
    & > div > svg > path {
      stroke: ${p => p.theme.colors.background.primary};
    }
  }
`

const Td = styled.div`
  display: flex;
  align-items: center;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    flex-basis: 200px;
  }
`

const MobileRowHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: ${p => `1px solid ${p.theme.colors.divider}`};
`

const MobileVaultRowWrapper = styled.div`
  padding: 12px;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  border: ${p => `1px solid ${p.theme.colors.divider}`};
  border-radius: 16px;
  margin-bottom: 8px;
`

const MobileRowItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ColumnContentLoading = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={150}
      height={25}
      viewBox="0 0 150 25"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="8" rx="3" ry="3" width="75" height="7" />
      <rect x="0" y="26" rx="3" ry="3" width="150" height="7" />
    </ContentLoader>
  )
}

const MobileColumnContentLoading = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={75}
      height={25}
      viewBox="0 0 75 25"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="8" rx="3" ry="3" width="75" height="7" />
      <rect x="0" y="26" rx="3" ry="3" width="75" height="7" />
    </ContentLoader>
  )
}

export const LpVaultRow = ({ displayInfo, isLast, isLoading }: LpVaultRowProps) => {
  const isMobile = useIsMobile()

  const { tvl, apr, deposited, earned } = displayInfo

  return (
    <Link
      to={`/${ROUTES.LP_VAULTS}/${displayInfo.coinX.displaySymbol}-${displayInfo.coinY.displaySymbol}`}
      key={`${displayInfo.coinX.displaySymbol}-${displayInfo.coinY.displaySymbol}`}
      style={{ textDecoration: 'none' }}
    >
      {isMobile ? (
        <MobileVaultRowWrapper key={`${displayInfo.coinX.displaySymbol}-${displayInfo.coinY.displaySymbol}`}>
          <>
            <MobileRowHeader>
              <div>
                <div style={{ display: 'flex', zIndex: 1 }}>
                  <img
                    src={displayInfo.coinX.iconUrl}
                    style={{ width: 24, height: 24, borderRadius: '50%' }}
                    alt={displayInfo.coinX.displaySymbol}
                  />
                  <img
                    src={displayInfo.coinY.iconUrl}
                    style={{ width: 24, height: 24, borderRadius: '50%' }}
                    alt={displayInfo.coinY.displaySymbol}
                  />
                </div>
                <Typography variant="body">{`${displayInfo.coinX.displaySymbol}-${displayInfo.coinY.displaySymbol}`}</Typography>
              </div>
              <Td style={{ display: 'block' }}>
                <Typography variant="body" color="secondary" style={{ textAlign: 'right' }}>
                  APR
                </Typography>
                <Typography variant="body" fontWeight="semiBold">
                  {isLoading ? <MobileColumnContentLoading /> : apr ? apr : '$0'}
                </Typography>
              </Td>
            </MobileRowHeader>
            <MobileRowItem style={{ paddingTop: 12 }}>
              <Typography variant="body" color="secondary" style={{ textAlign: 'right' }}>
                TVL
              </Typography>
              <Typography variant="body" fontWeight="semiBold">
                {isLoading ? <MobileColumnContentLoading /> : tvl ? getFormattedUsdValue(tvl) : '$0'}
              </Typography>
            </MobileRowItem>
            <MobileRowItem style={{ marginTop: 8 }}>
              <Typography variant="body" color="secondary" style={{ textAlign: 'right' }}>
                Deposited
              </Typography>
              <Typography variant="body" fontWeight="semiBold">
                {isLoading ? <MobileColumnContentLoading /> : deposited ? getFormattedUsdValue(deposited) : '$0'}
              </Typography>
            </MobileRowItem>
            <MobileRowItem style={{ marginTop: 8 }}>
              <Typography variant="body" color="secondary" style={{ textAlign: 'right' }}>
                Earned
              </Typography>
              <Typography variant="body" fontWeight="semiBold">
                {isLoading ? <MobileColumnContentLoading /> : earned ? getFormattedUsdValue(earned) : '$0'}
              </Typography>
            </MobileRowItem>
          </>
        </MobileVaultRowWrapper>
      ) : (
        <VaultRowWrapper key={`${displayInfo.coinX.displaySymbol}-${displayInfo.coinY.displaySymbol}`} $isLast={isLast}>
          <Td>
            <div style={{ display: 'flex', zIndex: 1 }}>
              <img
                src={displayInfo.coinX.iconUrl}
                style={{ width: 24, height: 24, borderRadius: '50%' }}
                alt={displayInfo.coinX.displaySymbol}
              />
            </div>
            <div style={{ position: 'absolute', display: 'flex', left: 56 }}>
              <img
                src={displayInfo.coinY.iconUrl}
                style={{ width: 24, height: 24, borderRadius: '50%' }}
                alt={displayInfo.coinY.displaySymbol}
              />
            </div>
            <Typography variant="label2" style={{ marginLeft: 32 }}>
              {`${displayInfo.coinX.displaySymbol}-${displayInfo.coinY.displaySymbol}`}
            </Typography>
          </Td>
          <Td>
            <Typography variant="label1">
              {isLoading ? <ColumnContentLoading /> : tvl ? getFormattedUsdValue(tvl) : '$0'}
            </Typography>
          </Td>
          <Td>
            <Typography variant="label1">{isLoading ? <ColumnContentLoading /> : apr ? apr : '$0'}</Typography>
          </Td>
          <Td>
            <Typography variant="label1">
              {isLoading ? <ColumnContentLoading /> : deposited ? getFormattedUsdValue(deposited) : '$0'}
            </Typography>
          </Td>
          <Td>
            <Typography variant="label1">
              {isLoading ? <ColumnContentLoading /> : earned ? getFormattedUsdValue(earned) : '$0'}
            </Typography>
          </Td>
          <IconNextContainer>
            <IconButtonNext />
          </IconNextContainer>
        </VaultRowWrapper>
      )}
    </Link>
  )
}
